import { useState, useEffect } from 'react'
import './SceneHierarchy.css'
import {
  DndContext,

  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  CollisionDetection,
  pointerWithin,
  rectIntersection,

} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SceneObject } from '../db/service'
import './SceneHierarchy.css'

interface SceneHierarchyProps {
  objects: SceneObject[]
  selectedObjectId: string | null
  onObjectSelect: (id: string) => void
  onObjectReorder: (draggedId: string, targetId: string | null, position: 'before' | 'after' | 'child') => void
  onObjectRename: (objectId: string, newName: string) => void
  onPrimitiveAdd: (type: 'cube' | 'sphere' | 'cylinder' | 'plane' | 'cone') => void
}

interface HierarchyNode {
  object: SceneObject
  children: HierarchyNode[]
  depth: number
}

interface DragData {
  id: string
  type: 'object'
  parentId: string | null
  depth: number
}

// Custom collision detection for hierarchical drag and drop
const hierarchicalCollisionDetection: CollisionDetection = (args) => {
  // First, let's see if there are any pointer intersections
  const pointerIntersections = pointerWithin(args)
  if (pointerIntersections.length > 0) {
    return pointerIntersections
  }

  // If there are no pointer intersections, return rectangle intersections
  const rectIntersections = rectIntersection(args)
  if (rectIntersections.length > 0) {
    return rectIntersections
  }

  // If no intersections at all, return empty array (will be treated as drop outside)
  return []
}

function SortableHierarchyItem({
  node,
  selectedObjectId,
  onObjectSelect,
  onObjectRename,
  editingId,
  setEditingId,
  editingName,
  setEditingName,
  dropIntent
}: {
  node: HierarchyNode
  selectedObjectId: string | null
  onObjectSelect: (id: string) => void
  onObjectRename: (objectId: string, newName: string) => void
  editingId: string | null
  setEditingId: (id: string | null) => void
  editingName: string
  setEditingName: (name: string) => void
  dropIntent: { targetId: string; type: 'child' | 'sibling' | 'before' | 'after' } | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id: node.object.id,
    data: {
      type: 'object',
      parentId: node.object.parentId,
      depth: node.depth,
    } as DragData,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isSelected = selectedObjectId === node.object.id
  const isEditing = editingId === node.object.id
  const isDropTarget = isOver && active?.id !== node.object.id
  const currentDropIntent = dropIntent?.targetId === node.object.id ? dropIntent.type : null

  const startRename = (object: SceneObject) => {
    setEditingId(object.id)
    setEditingName(object.name || object.id)
  }

  const finishRename = () => {
    if (editingId && editingName.trim()) {
      onObjectRename(editingId, editingName.trim())
    }
    setEditingId(null)
    setEditingName('')
  }

  const cancelRename = () => {
    setEditingId(null)
    setEditingName('')
  }

  const getObjectIcon = (type: string): string => {
    switch (type) {
      case 'cube': return '⬜'
      case 'sphere': return '🔴'
      case 'cylinder': return '🔵'
      case 'cone': return '🔺'
      case 'plane': return '▭'
      default: return '📦'
    }
  }

  const classes = [
    'hierarchy-item',
    isSelected && 'selected',
    isDragging && 'dragging',
    isDropTarget && currentDropIntent === 'child' && 'drop-target-child',
    isDropTarget && (currentDropIntent === 'sibling' || currentDropIntent === 'before') && 'drop-target-before',
    isDropTarget && currentDropIntent === 'after' && 'drop-target-after',
  ].filter(Boolean).join(' ')

  return (
    <div className="hierarchy-node">
      <div
        ref={setNodeRef}
        className={classes}
        style={{
          ...style,
          paddingLeft: `${node.depth * 20 + 8}px`
        }}
        data-sortable-id={node.object.id}
        {...attributes}
        {...listeners}
        onClick={() => !isEditing && onObjectSelect(node.object.id)}
        onDoubleClick={() => startRename(node.object)}
      >
        {node.children.length > 0 && (
          <span className="expand-icon">▶</span>
        )}

        <span className={`object-icon object-icon-${node.object.type}`}>
          {getObjectIcon(node.object.type)}
        </span>

        {isEditing ? (
          <input
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={(e) => {
              // Only finish rename if we're not pressing escape
              if (e.relatedTarget?.getAttribute('data-action') !== 'cancel') {
                finishRename()
              }
            }}
            onKeyDown={(e) => {
              e.stopPropagation() // Prevent drag events
              if (e.key === 'Enter') {
                finishRename()
              }
              if (e.key === 'Escape') {
                e.currentTarget.setAttribute('data-action', 'cancel')
                cancelRename()
              }
            }}
            onClick={(e) => e.stopPropagation()} // Prevent selection change
            autoFocus
            className="rename-input"
          />
        ) : (
          <span className="object-name">{node.object.name || node.object.id}</span>
        )}

        <span className="object-type">{node.object.type}</span>
      </div>

      {node.children.length > 0 && (
        <div className="hierarchy-children">
          <SortableContext
            items={node.children.map(child => child.object.id)}
            strategy={verticalListSortingStrategy}
          >
            {node.children.map(child => (
              <SortableHierarchyItem
                key={child.object.id}
                node={child}
                selectedObjectId={selectedObjectId}
                onObjectSelect={onObjectSelect}
                onObjectRename={onObjectRename}
                editingId={editingId}
                setEditingId={setEditingId}
                editingName={editingName}
                setEditingName={setEditingName}
                dropIntent={dropIntent}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  )
}

export function SceneHierarchy({
  objects,
  selectedObjectId,
  onObjectSelect,
  onObjectReorder,
  onObjectRename,
  onPrimitiveAdd
}: SceneHierarchyProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [dropIntent, setDropIntent] = useState<{
    targetId: string
    type: 'child' | 'sibling' | 'before' | 'after'
  } | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Build hierarchy tree
  const buildHierarchy = (): HierarchyNode[] => {
    const rootObjects = objects.filter(obj => !obj.parentId)

    const buildNode = (object: SceneObject, depth: number = 0): HierarchyNode => {
      const children = objects
        .filter(child => child.parentId === object.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(child => buildNode(child, depth + 1))

      return { object, children, depth }
    }

    return rootObjects
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(obj => buildNode(obj))
  }

  const hierarchy = buildHierarchy()
  const flattenedItems = objects.map(obj => obj.id)

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    console.log('=== DND-KIT DRAG START ===')
    console.log('Starting drag for:', event.active.id)
    
    // Verify the object exists before starting drag
    const draggedObject = objects.find(obj => obj.id === event.active.id)
    if (!draggedObject) {
      console.error('CRITICAL: Cannot start drag - object not found!', event.active.id)
      return
    }
    
    console.log('Dragged object:', draggedObject)
    setActiveId(event.active.id as string)
  }

  // Handle drag over for visual feedback
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    
    if (!over || !active || active.id === over.id) {
      setDropIntent(null)
      return
    }

    const overData = over.data.current

    if (overData?.type === 'object') {
      // Try to get position for visual feedback
      const overElement = document.querySelector(`[data-sortable-id="${over.id}"]`) as HTMLElement
      if (overElement) {
        // Use current mouse position for real-time feedback
        const rect = overElement.getBoundingClientRect()
        let mouseY = rect.top + rect.height / 2 // Default to center
        
        // Get mouse position from the drag over event
        const dragOverEvent = event as any
        if (dragOverEvent.activatorEvent && dragOverEvent.activatorEvent.clientY !== undefined) {
          mouseY = dragOverEvent.activatorEvent.clientY
        }
        
        const relativeY = (mouseY - rect.top) / rect.height
        
        // Same logic as drag end for consistent feedback
        if (relativeY < 0.25) {
          setDropIntent({ targetId: over.id as string, type: 'before' })
        } else if (relativeY > 0.75) {
          setDropIntent({ targetId: over.id as string, type: 'after' })
        } else {
          setDropIntent({ targetId: over.id as string, type: 'child' })
        }
      } else {
        // Fallback to child
        setDropIntent({ targetId: over.id as string, type: 'child' })
      }
    } else {
      setDropIntent(null)
    }
  }

  // Handle drag end - this is where we determine the new position
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    setDropIntent(null)

    if (!active.id) {
      console.warn('DragEnd: No active.id found!')
      return
    }

    console.log('=== DND-KIT DRAG END ===')
    console.log('Active (dragged):', { id: active.id })
    console.log('Over (target):', { id: over?.id, data: over?.data })
    console.log('Event details:', {
      activeRect: active.rect,
      overRect: over?.rect,
      delta: event.delta,
      collisions: event.collisions
    })

    // Check if the dragged object actually exists in our objects array
    const draggedObject = objects.find(obj => obj.id === active.id)
    if (!draggedObject) {
      console.error('CRITICAL: Dragged object not found in objects array!', active.id)
      console.log('Available objects:', objects.map(obj => ({ id: obj.id, name: obj.name })))
      return
    }

    // If no collision detected, treat as drop outside panel (reset to root)
    if (!over) {
      console.log('Dropped outside panel - making root object (NOT deleting)')
      console.log('Object before reorder:', draggedObject)
      
      // Double-check the object still exists before calling reorder
      const finalCheck = objects.find(obj => obj.id === active.id)
      if (!finalCheck) {
        console.error('ABORT: Object disappeared before reorder!', active.id)
        return
      }
      
      onObjectReorder(active.id as string, null, 'child')
      return
    }

    const overData = over.data.current

    if (overData?.type === 'object') {
      const activeObject = objects.find(obj => obj.id === active.id)
      const overObject = objects.find(obj => obj.id === over.id)
      
      if (!activeObject || !overObject) return

      // Check if we're trying to make a parent a child of its own descendant
      const isDescendant = (potentialAncestor: string, potentialDescendant: string): boolean => {
        const descendant = objects.find(obj => obj.id === potentialDescendant)
        if (!descendant || !descendant.parentId) return false
        if (descendant.parentId === potentialAncestor) return true
        return isDescendant(potentialAncestor, descendant.parentId)
      }

      if (isDescendant(active.id as string, over.id as string)) {
        console.log('Cannot make ancestor a child of descendant')
        return
      }

      // Get position-based detection using the DOM element
      const overElement = document.querySelector(`[data-sortable-id="${over.id}"]`) as HTMLElement
      if (overElement) {
        // Use the current mouse position to determine drop intent
        const rect = overElement.getBoundingClientRect()
        const centerY = rect.top + rect.height / 2
        
        // Use pointer position from the event (fallback to center if not available)
        let mouseY = centerY
        
        // Try to get mouse position from event - use a more robust approach
        if (event.activatorEvent) {
          const pointerEvent = event.activatorEvent as any
          if (pointerEvent.clientY !== undefined) {
            mouseY = pointerEvent.clientY
          }
        }
        
        const relativeY = (mouseY - rect.top) / rect.height
        
        console.log('Position detection:', { 
          mouseY, 
          rectTop: rect.top, 
          rectHeight: rect.height, 
          relativeY: relativeY.toFixed(2) 
        })

        // Position-based logic:
        // Top 25% = reorder before
        // Bottom 25% = reorder after  
        // Middle 50% = make child
        if (relativeY < 0.25) {
          console.log('Reordering before (top 25%)')
          onObjectReorder(active.id as string, over.id as string, 'before')
        } else if (relativeY > 0.75) {
          console.log('Reordering after (bottom 25%)')
          onObjectReorder(active.id as string, over.id as string, 'after')
        } else {
          console.log('Making parent-child relationship (center 50%)')
          onObjectReorder(active.id as string, over.id as string, 'child')
        }
      } else {
        // Fallback to child if we can't detect position
        console.log('Making parent-child relationship (fallback)')
        onObjectReorder(active.id as string, over.id as string, 'child')
      }
    } else {
      // Dropped on empty space in hierarchy - make it a root object (NOT deleting)
      console.log('Dropped on empty space - making root object (NOT deleting)')
      onObjectReorder(active.id as string, null, 'child')
    }
  }

  // Handle click outside to close add menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAddMenu && !(event.target as Element)?.closest('.add-object-dropdown')) {
        setShowAddMenu(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showAddMenu])

  const activeObject = activeId ? objects.find(obj => obj.id === activeId) : null

  return (
    <div className="scene-hierarchy">
      <div className="hierarchy-header">
        <h3>Scene Hierarchy</h3>
        <div className="add-object-dropdown">
          <button
            className="add-object-button"
            onClick={(e) => {
              e.stopPropagation()
              setShowAddMenu(!showAddMenu)
            }}
          >
            +
          </button>
          {showAddMenu && (
            <div className="add-object-menu" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => { onPrimitiveAdd('cube'); setShowAddMenu(false) }}>
                ⬜ Add Cube
              </button>
              <button onClick={() => { onPrimitiveAdd('sphere'); setShowAddMenu(false) }}>
                🔴 Add Sphere
              </button>
              <button onClick={() => { onPrimitiveAdd('cylinder'); setShowAddMenu(false) }}>
                🔵 Add Cylinder
              </button>
              <button onClick={() => { onPrimitiveAdd('cone'); setShowAddMenu(false) }}>
                🔺 Add Cone
              </button>
              <button onClick={() => { onPrimitiveAdd('plane'); setShowAddMenu(false) }}>
                ▭ Add Plane
              </button>
            </div>
          )}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={hierarchicalCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          className="hierarchy-tree"
          onDragOver={(e) => {
            e.preventDefault()
            setDropIntent(null)
          }}
        >
          {hierarchy.length === 0 ? (
            <div className="empty-hierarchy">
              No objects in scene. Use the + button to add objects.
            </div>
          ) : (
            <>
              <SortableContext
                items={flattenedItems}
                strategy={verticalListSortingStrategy}
              >
                {hierarchy.map(node => (
                  <SortableHierarchyItem
                    key={node.object.id}
                    node={node}
                    selectedObjectId={selectedObjectId}
                    onObjectSelect={onObjectSelect}
                    onObjectRename={onObjectRename}
                    editingId={editingId}
                    setEditingId={setEditingId}
                    editingName={editingName}
                    setEditingName={setEditingName}
                    dropIntent={dropIntent}
                  />
                ))}
              </SortableContext>

              {/* Remove the explicit drop zone - empty space will handle this */}
            </>
          )}
        </div>

        <DragOverlay>
          {activeObject ? (
            <div className="hierarchy-item dragging" style={{
              padding: '6px 8px',
              background: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span className={`object-icon object-icon-${activeObject.type}`}>
                {activeObject.type === 'cube' ? '⬜' :
                 activeObject.type === 'sphere' ? '🔴' :
                 activeObject.type === 'cylinder' ? '🔵' :
                 activeObject.type === 'plane' ? '▭' : '📦'}
              </span>
              <span className="object-name">{activeObject.name || activeObject.id}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
} 